import { useApplicationDocuments } from '@/hooks/useApplicationDocuments';
import { parseDocumentMetadata, mergeDocumentSelection } from '@/utils/applicationDocuments';
import { ExistingDocumentLink } from './ExistingDocumentLink';
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { FileText, Upload, CheckCircle, XCircle, Loader } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';

interface DocumentsChecklistStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
  draftApplicationId?: number | null;
}

interface DocumentFile {
  type: string;
  file: File | null;
  uploaded: boolean;
  uploading: boolean;
  documentId?: string | number;
  fileName?: string;
}

export const DocumentsChecklistStep: React.FC<DocumentsChecklistStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
  draftApplicationId,
}) => {
  const [documents, setDocuments] = useState<Record<string, DocumentFile>>({
    convictionCheck: { type: 'convictionCheck', file: null, uploaded: false, uploading: false },
    passport: { type: 'passport', file: null, uploaded: false, uploading: false },
    transcripts: { type: 'transcripts', file: null, uploaded: false, uploading: false },
    certificates: { type: 'certificates', file: null, uploaded: false, uploading: false },
    workHistory: { type: 'workHistory', file: null, uploaded: false, uploading: false },
    ieltsToefl: { type: 'ieltsToefl', file: null, uploaded: false, uploading: false },
  });
  const existing = useApplicationDocuments(draftApplicationId, formData.documents);
  const isLoadingDocuments = existing.loading;
  const loadedApplicationId = useRef(draftApplicationId);
  const otherAttachments = [
    ...existing.unmatched,
    ...Object.entries(existing.matched)
      .filter(([type]) => !['convictionCheck', 'passport', 'transcripts', 'certificates', 'workHistory', 'ieltsToefl'].includes(type))
      .map(([, file]) => file),
  ];
  useEffect(() => {
    const applicationChanged = loadedApplicationId.current !== draftApplicationId;
    loadedApplicationId.current = draftApplicationId;
    if (existing.loading && !applicationChanged) return;
    setDocuments(previous => Object.fromEntries(Object.keys(previous).map(type => {
      const file = existing.matched[type];
      const loaded = { type, file: null, uploaded: Boolean(file), uploading: false,
        documentId: file?.id, fileName: file?.fileName };
      return [type, mergeDocumentSelection(previous[type], loaded, applicationChanged)];
    })));
  }, [draftApplicationId, formData.documents, existing.loading]);

  const uploadInProgress = Object.values(documents).some(document => document.uploading);

  const handleFileChange = (type: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setDocuments(prev => ({
        ...prev,
        [type]: { 
          ...prev[type], 
          file, 
          uploaded: false, // Reset uploaded status when new file is selected
          fileName: undefined, // Clear old filename
          documentId: undefined // Clear old document ID
        }
      }));
    }
  };

  const handleFileRemove = (type: string) => {
    setDocuments(prev => ({
      ...prev,
      [type]: { ...prev[type], file: null, uploaded: false }
    }));
  };

  const getCategoryForDocument = (type: string): string => {
    const categoryMap: Record<string, string> = {
      convictionCheck: 'other',
      passport: 'identity',
      transcripts: 'academic',
      certificates: 'academic',
      workHistory: 'other',
      ieltsToefl: 'test-scores',
    };
    return categoryMap[type] || 'other';
  };

  const handleUpload = async (type: string) => {
    const doc = documents[type];
    if (!doc.file || uploadInProgress || existing.loading || existing.error) return;
    let currentDocuments: Record<string, any>;
    try { currentDocuments = parseDocumentMetadata(formData.documents); }
    catch { toast.error('Saved document information needs recovery before uploading. Existing attachments are listed below.'); return; }

    setDocuments(prev => ({
      ...prev,
      [type]: { ...prev[type], uploading: true }
    }));

    try {
      const category = getCategoryForDocument(type);
      const response = await agentApi.uploadDocument(
        doc.file,
        category,
        draftApplicationId || undefined
      );

      if (response.success && response.data) {
        // Get old document ID if replacing
        const oldDocumentId = documents[type].documentId;
        
        setDocuments(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            uploaded: true,
            uploading: false,
            documentId: response.data.id,
            fileName: response.data.fileName || response.data.originalName,
            file: null // Clear the file object after successful upload
          }
        }));

        // Update form data with document information (store in documents field)
        // This will replace the old document entry for this type

        currentDocuments[type] = {
          id: response.data.id,
          fileName: response.data.fileName || response.data.originalName,
          category: category,
          uploadedAt: new Date().toISOString(),
          previousId: oldDocumentId // Keep track of old ID for potential cleanup
        };
        updateFormData({ documents: JSON.stringify(currentDocuments) });

        if (type === 'passport' && errors?.documents) {
          setErrors({ ...errors, documents: undefined });
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error(`Error uploading ${type}:`, error);
      setDocuments(prev => ({
        ...prev,
        [type]: { ...prev[type], uploading: false }
      }));
      // Prefer backend error message (e.g. "Student profile not found") so the user sees a clear reason
      const backendError = error?.response?.data?.error;
      const friendlyMessage = backendError
        ? backendError
        : (error?.message?.includes('status code')
          ? 'Something went wrong. Please try again.'
          : (error?.message || 'Unknown error'));
      const docLabel = documentItems.find(d => d.key === type)?.label || type;
      toast.error(`Failed to upload ${docLabel}: ${friendlyMessage}`);
    }
  };

  const documentItems = [
    {
      key: 'convictionCheck',
      label: 'Copy of Conviction Check',
      required: formData.convictionChecks,
      description: 'Required if conviction checks are applicable'
    },
    {
      key: 'passport',
      label: 'Copy of Passport',
      required: true,
      description: 'Current valid passport (photo page)'
    },
    {
      key: 'transcripts',
      label: 'Copies of Certified Transcripts',
      required: false,
      description: 'Academic transcripts with certification'
    },
    {
      key: 'certificates',
      label: 'Copies of Certificates Awarded',
      required: false,
      description: 'Academic certificates and awards'
    },
    {
      key: 'workHistory',
      label: 'Work History Documents (if applicable)',
      required: false,
      description: 'Additional work history documentation'
    },
    {
      key: 'ieltsToefl',
      label: 'Evidence of IELTS/PTE Score',
      required: formData.hasEnglishScores,
      description: 'Required if English test scores are provided'
    },
  ];

  return (
    <div>
      <h5 className="fw-bold mb-3">APPLICATION CHECKLIST - For student/agent to complete</h5>
      <p className="text-muted mb-3">
        <small>Verified documents are required. Please upload the following documents as applicable.</small>
      </p>

      {errors?.documents && (
        <div className="alert alert-danger" role="alert">
          {errors.documents}
        </div>
      )}

      {isLoadingDocuments && (
        <div className="text-center py-2">
          <Loader className="spinner-border spinner-border-sm me-2" />
          <span>Loading documents...</span>
        </div>
      )}

      {existing.error && <div className="alert alert-danger" role="alert">Could not load existing documents. <button type="button" onClick={existing.retry}>Retry</button></div>}
      {existing.metadataError && <div className="alert alert-warning" role="alert">Saved document information could not be read. Existing attachments are retained; contact support to restore their checklist associations.</div>}
      {otherAttachments.length > 0 && <div className="mb-3"><h6>Other existing attachments</h6>{otherAttachments.map(file => <div key={file.id}><ExistingDocumentLink applicationId={draftApplicationId} document={file} /></div>)}</div>}
      {!existing.error && !existing.loading && <div className="row">
        {documentItems.map((item) => {
          const doc = documents[item.key];
          const isRequired = item.required;
          
          return (
            <div key={item.key} className="col-md-6 mb-3">
              <div className="card border">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div className="flex-grow-1">
                      <label className="form-label fw-semibold mb-1">
                        {item.label}
                        {isRequired && <span className="text-danger ms-1">*</span>}
                      </label>
                      <p className="text-muted small mb-2">{item.description}</p>
                    </div>
                    {doc.uploaded && (
                      <CheckCircle size={20} className="text-success" />
                    )}
                  </div>

                  {/* Show existing uploaded document */}
                  {doc.uploaded && doc.fileName && !doc.file && (
                    <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded mb-2">
                      <div className="d-flex align-items-center">
                        <FileText size={16} className="text-primary me-2" />
                        <ExistingDocumentLink applicationId={draftApplicationId} document={{ id: doc.documentId!, fileName: doc.fileName }} />
                        <span className="text-muted ms-2 small">(Uploaded)</span>
                      </div>
                    </div>
                  )}

                  {/* Show new file selected (not yet uploaded) */}
                  {doc.file && (
                    <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded mb-2">
                      <div className="d-flex align-items-center">
                        <FileText size={16} className="text-primary me-2" />
                        <span className="small">{doc.file.name}</span>
                        <span className="text-muted ms-2 small">
                          ({(doc.file.size / 1024).toFixed(1)} KB)
                        </span>
                        {doc.uploaded && doc.fileName && (
                          <span className="badge bg-warning ms-2">Replacing existing file</span>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUpload(item.key)}
                          disabled={uploadInProgress || existing.loading || existing.error || existing.metadataError}
                        >
                          {doc.uploading ? (
                            <>
                              <Loader size={14} className="me-1 spinner-border spinner-border-sm" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={14} className="me-1" />
                              {doc.uploaded ? 'Replace' : 'Upload'}
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleFileRemove(item.key)}
                          disabled={uploadInProgress || existing.loading || existing.error || existing.metadataError}
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* File input - always show to allow selection/replacement */}
                  <div>
                    <input
                      disabled={uploadInProgress || existing.loading || existing.error || existing.metadataError}
                      type="file"
                      className="form-control form-control-sm"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => handleFileChange(item.key, e)}
                      id={`file-${item.key}`}
                    />
                    <small className="text-muted d-block mt-1">
                      {doc.uploaded && doc.fileName 
                        ? 'Select a new file to replace the existing one' 
                        : 'Accepted formats: PDF, JPG, PNG, DOC, DOCX'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      }

      <div className="alert alert-info mt-4">
        <strong>Note:</strong> You can upload documents now or save as draft and upload them later. 
        All required documents must be uploaded before final submission.
      </div>
    </div>
  );
};

